import { Alert, Anchor, Badge, Button, Card, Group, List, Paper, RingProgress, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconArrowLeft, IconBook, IconCheck, IconLock, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { api } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import type { AttemptResult } from '../../lib/types';
import { QueryState } from '../../ui/QueryState';

export function ResultPage() {
  const attemptId = Number(useParams().attemptId);
  const q = useQuery({ queryKey: ['result', attemptId], queryFn: () => api.get<AttemptResult>(`/me/quiz-attempts/${attemptId}`) });
  return (
    <QueryState loading={q.isLoading} error={q.error}>
      {() => {
        const r = q.data!;
        const color = r.passed ? 'teal' : 'red';
        return (
          <Stack maw={820} mx="auto">
            <Anchor component={Link} to={`/learn/${r.assignmentId}`} size="sm"><IconArrowLeft size={14} style={{ verticalAlign: -2 }} /> К курсу</Anchor>
            <Card withBorder radius="lg" padding="xl">
              <Group gap="xl" wrap="wrap">
                <RingProgress size={140} thickness={12} roundCaps sections={[{ value: (r.correctCount / r.questionCount) * 100, color }]} label={<Text ta="center" fw={800} size="xl">{r.correctCount}/{r.questionCount}</Text>} />
                <Stack gap={6} style={{ flex: 1, minWidth: 220 }}>
                  <Badge color={color} size="lg" variant="light" w="fit-content">{r.passed ? 'Тест сдан' : 'Тест не сдан'}</Badge>
                  <Title order={3}>{r.title}</Title>
                  <Text size="sm">Верных ответов: <b>{r.correctCount} из {r.questionCount}</b> · нужно не меньше {r.passCount}</Text>
                  <Text size="sm" c="dimmed">Попытка {r.attemptNo} · {fmtDateTime(r.submittedAt)}</Text>
                </Stack>
              </Group>
            </Card>

            {!r.passed && r.attemptsLeft > 0 && (
              <Alert color="orange" title={`Осталось попыток: ${r.attemptsLeft}`} icon={<IconBook />}>
                <Text size="sm">Правильные ответы и разбор откроются после сдачи или после последней попытки. Следующая попытка соберёт вопросы из пула заново.</Text>
                {r.repeatLessons.length > 0 && (
                  <>
                    <Text size="sm" fw={500} mt="xs">Повторите уроки:</Text>
                    <List size="sm">{r.repeatLessons.map((l) => <List.Item key={l.id}><Anchor component={Link} to={`/learn/${r.assignmentId}/lesson/${l.id}`}>{l.title}</Anchor></List.Item>)}</List>
                  </>
                )}
                <Button mt="sm" color="orange" component={Link} to={r.repeatLessons[0] ? `/learn/${r.assignmentId}/lesson/${r.repeatLessons[0].id}` : `/learn/${r.assignmentId}`}>Повторить материал</Button>
              </Alert>
            )}
            {!r.passed && r.attemptsLeft === 0 && (
              <Alert color="red" icon={<IconLock />} title="Попытки закончились">Ниже полный разбор. Новые попытки добавляет ответственный за назначения в команде обучения.</Alert>
            )}

            {r.fullReview && r.items && (
              <>
                <Title order={4} mt="sm">Разбор ответов</Title>
                {r.items.map((it) => (
                  <Paper key={it.n} withBorder radius="lg" p="md" style={{ borderLeft: `4px solid var(--mantine-color-${it.isCorrect ? 'teal' : 'red'}-5)` }}>
                    <Group gap="sm" align="flex-start" wrap="nowrap">
                      <ThemeIcon radius="xl" size="sm" color={it.isCorrect ? 'teal' : 'red'} mt={2}>{it.isCorrect ? <IconCheck size={13} /> : <IconX size={13} />}</ThemeIcon>
                      <Stack gap={6} style={{ flex: 1 }}>
                        <Text fw={600} size="sm">{it.n}. {it.text}</Text>
                        <Text size="sm">Ваш ответ: <Text span c={it.isCorrect ? 'teal' : 'red'} fw={500}>{it.chosen ?? 'нет ответа'}</Text></Text>
                        {!it.isCorrect && <Text size="sm">Правильный ответ: <Text span c="teal" fw={500}>{it.correctAnswer}</Text></Text>}
                        {it.explanation && <Text size="sm" c="dimmed">{it.explanation}</Text>}
                      </Stack>
                    </Group>
                  </Paper>
                ))}
              </>
            )}
          </Stack>
        );
      }}
    </QueryState>
  );
}
